/**
 * Định nghĩa SFX bằng code (B3) — 20 hiệu ứng theo src/data/am-thanh.json.
 *
 * Thuần 100%: mỗi SFX là danh sách sự kiện (oscillator / nhiễu) với tham số
 * cụ thể, KHÔNG WebAudio. Engine (engine.ts) render ra âm thanh thật.
 * Deterministic — test được bằng vitest/node.
 */

export type KieuSongSfx = 'sine' | 'triangle' | 'square' | 'sawtooth';

/** Một nốt oscillator: tần số quét từ đầu đến cuối. */
export interface SuKienOsc {
  loai: 'osc';
  /** Giây, tính từ lúc phát SFX. */
  batDau: number;
  thoiLuong: number;
  tanSoDau: number;
  tanSoCuoi: number;
  kieuSong: KieuSongSfx;
  /** Gain đỉnh 0..1. */
  doDay: number;
}

/** Một đợt nhiễu (tiền xu lách cách, mưa, sóng, whoosh...). */
export interface SuKienNhieu {
  loai: 'nhieu';
  batDau: number;
  thoiLuong: number;
  tanSoLoc: number;
  tanSoLocCuoi: number;
  doDay: number;
  /** 'xung' = đánh nhanh rồi tắt; 'song' = dâng lên rồi rút (sóng/gió). */
  dangBao: 'xung' | 'song';
}

export type SuKienSfx = SuKienOsc | SuKienNhieu;

/** Định nghĩa đầy đủ một SFX. */
export interface DinhNghiaSfx {
  id: string;
  moTa: string;
  suKien: SuKienSfx[];
  /** Độ dài toàn bộ (giây) — engine dùng để dừng đúng lúc. */
  tongThoiGian: number;
  /** true = phát lặp vòng (mưa). Engine loop riêng. */
  lapVong?: boolean;
}

function osc(
  batDau: number,
  thoiLuong: number,
  tanSoDau: number,
  tanSoCuoi: number,
  kieuSong: KieuSongSfx,
  doDay: number,
): SuKienOsc {
  return { loai: 'osc', batDau, thoiLuong, tanSoDau, tanSoCuoi, kieuSong, doDay };
}

function nhieu(
  batDau: number,
  thoiLuong: number,
  tanSoLoc: number,
  tanSoLocCuoi: number,
  doDay: number,
  dangBao: 'xung' | 'song' = 'xung',
): SuKienNhieu {
  return { loai: 'nhieu', batDau, thoiLuong, tanSoLoc, tanSoLocCuoi, doDay, dangBao };
}

function dinhNghia(id: string, moTa: string, suKien: SuKienSfx[], lapVong = false): DinhNghiaSfx {
  const tongThoiGian = Math.max(...suKien.map((s) => s.batDau + s.thoiLuong));
  return { id, moTa, suKien, tongThoiGian, ...(lapVong ? { lapVong: true as const } : {}) };
}

/** Đủ 20 SFX theo am-thanh.json — thứ tự khớp file dữ liệu. */
const DANH_SACH: DinhNghiaSfx[] = [
  dinhNghia('sfx-ban-hang', 'Bán được hàng: "ka-ching" máy tính tiền', [
    osc(0, 0.09, 1318, 1318, 'triangle', 0.16),
    osc(0.08, 0.14, 1568, 1568, 'triangle', 0.16),
    nhieu(0, 0.05, 6000, 6000, 0.08),
  ]),
  dinhNghia('sfx-nhan-tien', 'Nhận tiền: 2 tiếng xu', [
    osc(0, 0.06, 988, 988, 'square', 0.1),
    osc(0.07, 0.16, 1319, 1175, 'square', 0.1),
  ]),
  dinhNghia('sfx-mo-cua', 'Mở cửa buổi sáng: chuông cửa 3 nốt', [
    osc(0, 0.18, 1047, 1047, 'sine', 0.15),
    osc(0.16, 0.18, 1319, 1319, 'sine', 0.15),
    osc(0.32, 0.3, 1568, 1568, 'sine', 0.15),
  ]),
  dinhNghia('sfx-het-hang', 'Kệ hết hàng: 2 nốt trầm đi xuống', [
    osc(0, 0.16, 220, 196, 'square', 0.09),
    osc(0.15, 0.24, 165, 147, 'square', 0.09),
  ]),
  dinhNghia('sfx-nang-cap', 'Nâng cấp cửa hàng: fanfare đi lên', [
    osc(0, 0.12, 261.6, 261.6, 'sawtooth', 0.1),
    osc(0.11, 0.12, 329.6, 329.6, 'sawtooth', 0.1),
    osc(0.22, 0.12, 392, 392, 'sawtooth', 0.1),
    osc(0.33, 0.3, 523.3, 523.3, 'sawtooth', 0.12),
  ]),
  dinhNghia('sfx-nhan-nv', 'Nhận nhiệm vụ mới: thông báo 2 nốt', [
    osc(0, 0.12, 880, 880, 'sine', 0.13),
    osc(0.11, 0.2, 1174.7, 1174.7, 'sine', 0.13),
  ]),
  dinhNghia('sfx-hoan-thanh-nv', 'Hoàn thành nhiệm vụ: fanfare 5 nốt', [
    osc(0, 0.1, 523.3, 523.3, 'triangle', 0.14),
    osc(0.09, 0.1, 587.3, 587.3, 'triangle', 0.14),
    osc(0.18, 0.1, 659.3, 659.3, 'triangle', 0.14),
    osc(0.27, 0.1, 784, 784, 'triangle', 0.14),
    osc(0.36, 0.32, 1046.5, 1046.5, 'triangle', 0.15),
  ]),
  dinhNghia('sfx-don-lon', 'Đơn lớn mới đến: trống + kèn trầm', [
    nhieu(0, 0.18, 800, 300, 0.2),
    osc(0.02, 0.4, 174.6, 130.8, 'sawtooth', 0.12),
    osc(0.3, 0.3, 261.6, 261.6, 'sawtooth', 0.1),
  ]),
  dinhNghia('sfx-tang-qua', 'Tặng quà NPC: chime mềm', [
    osc(0, 0.35, 1046.5, 1040, 'sine', 0.12),
    osc(0.12, 0.4, 1568, 1560, 'sine', 0.1),
  ]),
  dinhNghia('sfx-thanh-tuu', 'Mở thành tựu: chime lớn rải hợp âm', [
    osc(0, 0.3, 523.3, 523.3, 'triangle', 0.13),
    osc(0.1, 0.3, 659.3, 659.3, 'triangle', 0.13),
    osc(0.2, 0.3, 784, 784, 'triangle', 0.13),
    osc(0.3, 0.45, 1046.5, 1046.5, 'triangle', 0.14),
    osc(0.4, 0.5, 1318.5, 1318.5, 'sine', 0.1),
  ]),
  dinhNghia('sfx-click', 'Bấm nút: blip ngắn', [osc(0, 0.05, 800, 700, 'sine', 0.08)]),
  dinhNghia('sfx-di-chuyen', 'Di chuyển giữa các khu: whoosh', [
    nhieu(0, 0.5, 400, 3500, 0.1, 'song'),
  ]),
  dinhNghia('sfx-mua-thanh-cong', 'Mua nội thất/cosmetic thành công: 3 nốt vui', [
    osc(0, 0.1, 392, 392, 'triangle', 0.12),
    osc(0.09, 0.1, 523.3, 523.3, 'triangle', 0.12),
    osc(0.18, 0.22, 659.3, 659.3, 'triangle', 0.13),
  ]),
  dinhNghia('sfx-loi', 'Thao tác lỗi: buzz trầm 2 nhịp', [
    osc(0, 0.14, 140, 130, 'square', 0.1),
    osc(0.18, 0.2, 140, 120, 'square', 0.1),
  ]),
  dinhNghia('sfx-dong-mo-bang', 'Đóng/mở bảng: 2 blip lên', [
    osc(0, 0.06, 660, 660, 'sine', 0.09),
    osc(0.06, 0.08, 440, 440, 'sine', 0.09),
  ]),
  dinhNghia('sfx-mua', 'Trời mưa: rào rào lặp vòng', [nhieu(0, 4, 1200, 900, 0.09, 'song')], true),
  dinhNghia('sfx-ca-phe-xong', 'Pha xong cà phê: "ding" + rót', [
    osc(0, 0.3, 1760, 1750, 'sine', 0.12),
    nhieu(0.05, 0.25, 2500, 1800, 0.05),
  ]),
  dinhNghia('sfx-thue-nhan-vien', 'Thuê nhân viên: đóng dấu', [
    nhieu(0, 0.08, 500, 300, 0.18),
    osc(0.02, 0.16, 196, 174, 'triangle', 0.12),
  ]),
  dinhNghia('sfx-mo-khu', 'Mở khu mới: rải lên + shimmer', [
    osc(0, 0.14, 392, 392, 'triangle', 0.12),
    osc(0.12, 0.14, 523.3, 523.3, 'triangle', 0.12),
    osc(0.24, 0.14, 659.3, 659.3, 'triangle', 0.12),
    osc(0.36, 0.4, 784, 784, 'triangle', 0.13),
    nhieu(0.3, 0.4, 5000, 8000, 0.05),
  ]),
  dinhNghia('sfx-luu-game', 'Lưu game: blip mềm', [osc(0, 0.1, 523.3, 587.3, 'sine', 0.08)]),
];

const THEO_ID = new Map(DANH_SACH.map((d) => [d.id, d]));

/** Lấy định nghĩa SFX theo id; null nếu id lạ. */
export function dinhNghiaSfx(id: string): DinhNghiaSfx | null {
  return THEO_ID.get(id) ?? null;
}

/** Đủ 20 SFX. */
export function danhSachSfx(): readonly DinhNghiaSfx[] {
  return DANH_SACH;
}
